import { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { supabase } from '@/lib/supabase';
import {
  type ScheduleItem,
  type ScheduleLink,
  type ScheduleSettings,
  type HolidayInfo,
  type ScheduleHealthSummary,
  calculateCriticalPath,
  computeDelayAndRisk,
  buildHealthSummary,
  isScheduleItemIncluded,
  normalizeScheduleItemRow,
  recoverAllExcludedScheduleItems,
} from '@/lib/scheduleUtils';

import { JPH_DEBUG, jphDebugLog } from '@/lib/jphDebug';

// Module-level cache to prevent redundant fetches when multiple components
// mount simultaneously for the same project (e.g. ProjectDetail + StagesTab + ScheduleTab)
const CACHE_TTL_MS = 30_000; // 30 seconds
export const scheduleCache = new Map<string, {
  items: ScheduleItem[];
  links: ScheduleLink[];
  timestamp: number;
}>();

function perfLog(label: string, ms: number) {
  if (JPH_DEBUG) {
    jphDebugLog(`[SchedulePerf] ${label}: ${ms.toFixed(1)}ms`);
  }
}

export interface UseProjectScheduleResult {
  items: ScheduleItem[];
  links: ScheduleLink[];
  // Loading phases
  itemsLoading: boolean;
  linksLoading: boolean;
  secondaryLoading: boolean;
  loading: boolean;
  error: string | null;
  reload: () => Promise<void>;
  // Optimistic setters
  setItems: React.Dispatch<React.SetStateAction<ScheduleItem[]>>;
  setLinks: React.Dispatch<React.SetStateAction<ScheduleLink[]>>;
  // Derived data
  criticalItemIds: Set<string>;
  projectEndDate: string | null;
  computedRisk: Map<string, { delay_status: ScheduleItem['delay_status']; schedule_risk_level: ScheduleItem['schedule_risk_level']; delayDays: number }>;
  healthSummary: ScheduleHealthSummary | null;
  includedItems: ScheduleItem[];
}

export function useProjectSchedule(
  projectId: string | undefined,
  settings: ScheduleSettings | null,
  holidays: HolidayInfo[]
): UseProjectScheduleResult {
  const cached = projectId ? scheduleCache.get(projectId) : undefined;
  const [items, setItems] = useState<ScheduleItem[]>(cached?.items || []);
  const [links, setLinks] = useState<ScheduleLink[]>(cached?.links || []);
  const [itemsLoading, setItemsLoading] = useState(!cached?.items?.length);
  const [linksLoading, setLinksLoading] = useState(!cached?.items?.length);
  const [error, setError] = useState<string | null>(null);

  const isLoadingRef = useRef(false);
  const lastProjectIdRef = useRef<string | undefined>(undefined);

  // Hydrate from cache immediately when route project changes.
  useEffect(() => {
    if (!projectId) {
      setItems([]);
      setLinks([]);
      setItemsLoading(false);
      setLinksLoading(false);
      setError(null);
      lastProjectIdRef.current = undefined;
      isLoadingRef.current = false;
      return;
    }

    isLoadingRef.current = false;
    const projectCache = scheduleCache.get(projectId);
    if (projectCache && projectCache.items.length > 0) {
      setItems(recoverAllExcludedScheduleItems(projectCache.items));
      setLinks(projectCache.links);
      setItemsLoading(false);
      setLinksLoading(false);
      lastProjectIdRef.current = projectId;
    } else if (lastProjectIdRef.current !== projectId) {
      setItemsLoading(true);
      setLinksLoading(true);
    }
  }, [projectId]);

  // ── Phase 1: Load items + links (critical) ──
  const loadCritical = useCallback(async (forceRefresh = false) => {
    if (!projectId) return;

    const projectCache = scheduleCache.get(projectId);
    if (!forceRefresh && projectCache && Date.now() - projectCache.timestamp < CACHE_TTL_MS) {
      if (projectCache.items.length > 0) {
        setItems(recoverAllExcludedScheduleItems(projectCache.items));
        setLinks(projectCache.links);
        setItemsLoading(false);
        setLinksLoading(false);
        setError(null);
        lastProjectIdRef.current = projectId;
        return;
      }
      // Do not trust cached empty results — they may come from a failed query.
    }

    if (isLoadingRef.current && !forceRefresh) return;

    isLoadingRef.current = true;
    const requestProjectId = projectId;
    setItemsLoading(true);
    setLinksLoading(true);

    const t0 = performance.now();

    try {
      const [itemsRes, linksRes] = await Promise.all([
        supabase
          .from('jphph_project_schedule_items')
          .select('id,project_id,source_template_item_id,task_no,sort_order,title,phase,main_stage,construction_sub_stage,duration_days,start_date,end_date,progress,is_complete,is_included,assigned_to,assigned_role,notes,manual_date_lock,is_critical,baseline_start_date,baseline_end_date,actual_start_date,actual_end_date,delay_reason,delay_status,schedule_risk_level,bar_color,bar_color_mode,gantt_color,created_at,updated_at,is_deleted')
          .eq('project_id', requestProjectId)
          .eq('is_deleted', false)
          .order('sort_order', { ascending: true }),
        supabase
          .from('jphph_project_schedule_links')
          .select('*')
          .eq('project_id', requestProjectId),
      ]);

      if (requestProjectId !== projectId) return;

      if (itemsRes.error) {
        console.error('[useProjectSchedule.loadCritical] jphph_project_schedule_items query failed', {
          function: 'loadCritical',
          table: 'jphph_project_schedule_items',
          projectId: requestProjectId,
          error: itemsRes.error,
        });
        setError(itemsRes.error.message || 'Failed to load schedule items');
        return;
      }

      const rawItems = (itemsRes.data ?? []) as ScheduleItem[];
      const newItems = recoverAllExcludedScheduleItems(rawItems.map(normalizeScheduleItemRow));
      const includedCount = newItems.filter(isScheduleItemIncluded).length;
      const excludedCount = newItems.filter((i) => i.is_included === false).length;
      console.log('[useProjectSchedule.loadCritical] schedule inclusion stats', {
        projectId: requestProjectId,
        table: 'jphph_project_schedule_items',
        total: newItems.length,
        included: includedCount,
        excluded: excludedCount,
        sample: newItems.slice(0, 5).map((i) => ({ id: i.id, title: i.title, is_included: i.is_included })),
      });

      if (linksRes.error) {
        console.error('[useProjectSchedule.loadCritical] jphph_project_schedule_links query failed', {
          function: 'loadCritical',
          table: 'jphph_project_schedule_links',
          projectId: requestProjectId,
          error: linksRes.error,
        });
        setItems(newItems);
        setError(linksRes.error.message || 'Failed to load schedule links');
        if (newItems.length > 0) {
          scheduleCache.set(requestProjectId, {
            items: newItems,
            links: scheduleCache.get(requestProjectId)?.links ?? [],
            timestamp: Date.now(),
          });
        }
        lastProjectIdRef.current = requestProjectId;
        return;
      }

      const newLinks = (linksRes.data ?? []) as ScheduleLink[];
      const t1 = performance.now();
      setItems(newItems);
      setLinks(newLinks);
      setError(null);
      scheduleCache.set(requestProjectId, { items: newItems, links: newLinks, timestamp: Date.now() });
      lastProjectIdRef.current = requestProjectId;
      perfLog('loadCritical', t1 - t0);
    } catch (err: unknown) {
      console.error('[useProjectSchedule.loadCritical] unexpected error', {
        function: 'loadCritical',
        table: 'jphph_project_schedule_items',
        projectId: requestProjectId,
        error: err,
      });
      setError(err instanceof Error ? err.message : 'Failed to load schedule');
    } finally {
      if (requestProjectId === projectId) {
        setItemsLoading(false);
        setLinksLoading(false);
      }
      isLoadingRef.current = false;
    }
  }, [projectId]);

  useEffect(() => {
    loadCritical();
  }, [loadCritical]);

  // ── Realtime: listen for schedule item updates on this project ──
  useEffect(() => {
    if (!projectId) return;
    const channel = supabase
      .channel(`schedule-items-${projectId}`)
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'jphph_project_schedule_items',
          filter: `project_id=eq.${projectId}`,
        },
        (payload) => {
          if (payload.eventType === 'UPDATE' && payload.new) {
            const updated = payload.new as Partial<ScheduleItem> & { is_deleted?: boolean };
            // If item was soft-deleted, remove it from local state
            if (updated.is_deleted) {
              setItems((prev) => prev.filter((item) => item.id !== updated.id));
              return;
            }
            setItems((prev) =>
              prev.map((item) =>
                item.id === updated.id
                  ? { ...item, ...updated }
                  : item
              )
            );
          } else if (payload.eventType === 'DELETE' && payload.old) {
            setItems((prev) => prev.filter((item) => item.id !== payload.old.id));
          } else if (payload.eventType === 'INSERT' && payload.new) {
            const inserted = payload.new as Partial<ScheduleItem> & { is_deleted?: boolean };
            if (inserted.is_deleted) return;
            setItems((prev) => [...prev, payload.new as ScheduleItem]);
          }
        }
      )
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }, [projectId]);

  const loading = itemsLoading || linksLoading;

  // ── Memoized effective settings ──
  const effectiveSettings: ScheduleSettings = useMemo(() => {
    if (settings) return settings;
    return {
      id: '',
      project_id: projectId || '',
      working_days: [1, 2, 3, 4, 5, 6],
      sunday_off: true,
      saturday_off: true,
      exclude_public_holidays: true,
      holiday_country: 'NZ',
      holiday_region: null,
      created_at: '',
      updated_at: '',
    };
  }, [settings, projectId]);

  const includedItems = useMemo(
    () => items.filter(isScheduleItemIncluded),
    [items]
  );

  // ── Critical path (memoized) ──
  const cpResult = useMemo(() => {
    const t0 = performance.now();
    if (items.length === 0 || !effectiveSettings) return null;
    const result = calculateCriticalPath(items, links, effectiveSettings, holidays);
    perfLog('criticalPath', performance.now() - t0);
    return result;
  }, [items, links, effectiveSettings, holidays]);

  // ── Delay / Risk (memoized) ──
  const delayRiskMap = useMemo(() => {
    const t0 = performance.now();
    if (items.length === 0 || !effectiveSettings) return new Map();
    const result = computeDelayAndRisk(items, effectiveSettings, holidays);
    perfLog('delayRisk', performance.now() - t0);
    return result;
  }, [items, effectiveSettings, holidays]);

  // ── Health (memoized) ──
  const health = useMemo(() => {
    const t0 = performance.now();
    if (items.length === 0) return null;
    const cp = cpResult || {
      criticalItemIds: new Set<string>(),
      projectEndDate: null,
      cycleWarning: null,
    };
    const result = buildHealthSummary(items, delayRiskMap, cp.criticalItemIds);
    perfLog('healthSummary', performance.now() - t0);
    return result;
  }, [items, delayRiskMap, cpResult]);

  return {
    items,
    links,
    itemsLoading,
    linksLoading,
    secondaryLoading: false,
    loading,
    error,
    reload: () => {
      if (projectId) scheduleCache.delete(projectId);
      return loadCritical(true);
    },
    setItems,
    setLinks,
    criticalItemIds: cpResult?.criticalItemIds || new Set(),
    projectEndDate: cpResult?.projectEndDate || null,
    computedRisk: delayRiskMap,
    healthSummary: health,
    includedItems,
  };
}
